// src/components/support/past/Tickets.js
import React, { useState } from "react";
import moment from "moment";
import { useAtom } from "jotai";

import {
    FileText,
    X,
    Camera,
    Utensils,
    Smartphone,
    Bus,
    Heart,
    BadgeDollarSign,
} from "lucide-react";

import { activeChildDetail } from "../../atomstore/store";
import { useRequestProcessor } from "../../requestProcessor";
import { fetchTickets } from "../../queryfunctions/queryfunctions";

import SpinnerLoading from "../common/SpinnerLoading";
import { DEFAULT_FORMAT } from "../common/FootprintDatePicker";

const ticketIssueGradients = {
    Accounts: "from-blue-400 to-blue-600",
    Appreciation: "from-pink-400 to-pink-600",
    CCTV: "from-purple-400 to-purple-600",
    Food: "from-orange-400 to-orange-600",
    "Mobile App": "from-indigo-400 to-indigo-600",
    Transport: "from-green-400 to-green-600",
    Others: "from-gray-400 to-gray-600",
    default: "from-cyan-400 to-cyan-500",
};

const ticketIssueIcons = {
    Accounts: <BadgeDollarSign size={30} className=" text-white" />,
    Appreciation: <Heart size={30} className="text-white" />,
    CCTV: <Camera size={30} className=" text-white" />,
    Food: <Utensils size={30} className=" text-white" />,
    "Mobile App": <Smartphone size={30} className=" text-white" />,
    Transport: <Bus size={30} className=" text-white" />,
    Others: <FileText size={30} className=" text-white" />,
    default: <FileText size={30} className=" text-white" />,
};

const statusMap = {
    2: { label: "Open", gradient: "from-green-400 to-green-600" },
    3: { label: "Pending", gradient: "from-yellow-400 to-yellow-600" },
    4: { label: "Resolved", gradient: "from-blue-400 to-blue-600" },
    5: { label: "Closed", gradient: "from-red-400 to-red-600" },
};

const Tickets = () => {
    const { useQueryWrapper } = useRequestProcessor();
    const [activeChild] = useAtom(activeChildDetail);
    const [selectedItem, setSelectedItem] = useState(null);

    const { data: tickets, isLoading } = useQueryWrapper(
        ["pastTickets", "ticket" + activeChild?.id],
        () => fetchTickets(activeChild)
    );

    return (
        <div className="pt-2 pb-20 space-y-4">
            {isLoading && <SpinnerLoading />}

            {!isLoading && (!tickets || tickets.length === 0) && (
                <div className="flex flex-col items-center justify-center py-16 text-center text-gray-600">
                    <FileText className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-base font-semibold">No Tickets Found</p>
                    <p className="text-xs text-gray-500 mt-1">
                        You have not raised any tickets.
                    </p>
                </div>
            )}

            {Array.isArray(tickets) &&
                tickets.map((item, index) => {
                    const statusInfo = statusMap[item.status];
                    const issueType = item.custom_fields?.issue_type;

                    return (
                        <div
                            key={index}
                            onClick={() => setSelectedItem(item)}
                            className="bg-white rounded-2xl p-4 shadow-sm border border-gray-200 
                                hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-pointer"
                        >

                            {/* 3-column layout */}
                            <div className="flex items-start justify-between space-x-4">

                                {/* Left: Icon */}
                                <div
                                    className={`
                                        p-3 rounded-xl bg-gradient-to-br shadow-sm
                                        ${ticketIssueGradients[issueType] || ticketIssueGradients.default}
                                    `}
                                >
                                    {ticketIssueIcons[issueType] || ticketIssueIcons.default}
                                </div>

                                {/* Middle: Content */}
                                <div className="flex-1">
                                    <h3 className="text-base font-semibold text-gray-900">
                                        {issueType || "Ticket"}
                                    </h3>

                                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                        {item.subject}
                                    </p>
                                </div>

                                {/* Right: Status + Date */}
                                <div className="flex flex-col items-end justify-start space-y-1 min-w-[90px]">

                                    <span
                                        className={`
                                            text-xs font-semibold px-3 py-1 rounded-full shadow-sm 
                                            bg-gradient-to-r text-white
                                            ${statusInfo?.gradient}
                                        `}
                                    >
                                        {statusInfo?.label}
                                    </span>

                                    <span className="text-[10px] text-gray-500 whitespace-nowrap">
                                        {moment(item.created_at).format(DEFAULT_FORMAT)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                })}

            {selectedItem && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-fadeIn">

                        <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-4 flex justify-between items-center">
                            <h3 className="text-white font-semibold text-lg">Ticket Details</h3>
                            <button onClick={() => setSelectedItem(null)}>
                                <X className="w-6 h-6 text-white" />
                            </button>
                        </div>

                        <div className="p-4 space-y-3">
                            <h4 className="text-gray-900 font-semibold">
                                {selectedItem.custom_fields?.issue_type}
                            </h4>

                            <p className="text-sm text-gray-700">
                                {selectedItem.subject || "No subject provided."}
                            </p>

                            <p className="text-xs text-gray-500">
                                Created on:{" "}
                                {moment(selectedItem.created_at).format(DEFAULT_FORMAT)}
                            </p>
                        </div>

                        {/* <div className="p-3 bg-gray-50 text-right">
                            <button
                                onClick={() => setSelectedItem(null)}
                                className="text-sm font-semibold text-orange-600"
                            >
                                Close
                            </button>
                        </div> */}

                    </div>
                </div>
            )}
        </div>
    );
};

export default Tickets;
